import getHandInfo from '@/services/simulator/get-info-from-combined-cards/get-hand-info';
import getDrawInfo from '@/services/simulator/get-info-from-combined-cards/get-draw-info';
import setSpecificHandInfo from '@/services/simulator/get-info-from-combined-cards/set-specific-hand-info';
import utilities from '@/services/utilities/utilities';
import _ from 'lodash';

export default class SpecificHand {
  constructor(specificHandString) {
    this.string = specificHandString;
    this.displayString = specificHandString[0].toUpperCase() + specificHandString[1] + specificHandString[2].toUpperCase() + specificHandString[3];
    this.firstCard = this.string[0] + this.string[1];
    this.secondCard = this.string[2] + this.string[3];
    this.combo = this.string[1] + this.string[3];
    this._weight = 0;
    this._maxPossibleWeight = 100;
    this._manuallySet = null;
    this.generalHand = null;
  };

  get possible() {
    return this.maxPossibleWeight > 0;
  };

  get inRange() {
    if (!this.possible) {
      throw new Error('Trying to check if a specific hand that isn\'t possible is in range.');
    }

    return this.weight > 0;
  };

  get weight() {
    return this._weight;
  };
  set weight(newWeight) {
    if (typeof newWeight !== 'number' && newWeight !== null) {
      throw new Error('Invalid value for newWeight.');
    }

    if (typeof newWeight === 'number') {
      if (
        (newWeight < 0) ||
        (newWeight > 100) ||
        (newWeight % 5 !== 0)
      ) {
        throw new Error('Invalid value for newWeight.');
      }
    }

    if (newWeight > this.maxPossibleWeight) {
      throw new Error('Trying to set a weight that is greater than the max possible weight.');
    }

    this._weight = newWeight;
  };

  get maxPossibleWeight() {
    return this._maxPossibleWeight;
  };
  set maxPossibleWeight(newMaxPossibleWeight) {
    if (
      (typeof newMaxPossibleWeight !== 'number') ||
      (newMaxPossibleWeight < 0) ||
      (newMaxPossibleWeight > 100) ||
      (newMaxPossibleWeight % 5 !== 0)
    ) {
      throw new Error('Invalid value for newMaxPossibleWeight.');
    }

    this._maxPossibleWeight = newMaxPossibleWeight;
  };

  get manuallySet() {
    return this._manuallySet;
  };
  set manuallySet(value) {
    if (value !== 'added' && value !== 'removed' && value !== false) {
      throw new Error('manuallySet needs to have a value of added, removed or false.');
    }

    if (value === 'added' && !this.inRange) {
      throw new Error('Can\'t set manuallySet to added for a specific hand that isn\'t in range.');
    }

    if (value === 'removed' && this.inRange) {
      throw new Error('Can\'t set manuallySet to removed for a specific hand that is in range.');
    }

    this._manuallySet = value;
  };

  get manuallyAdded() {
    return this.possible && this.inRange && this.manuallySet;
  };
  get manuallyRemoved() {
    return this.possible && !this.inRange && this.manuallySet;
  };

  get firstRank() {
    return this.string[0];
  };
  get secondRank() {
    return this.string[2];
  };
  get hasFiveFourThreeOrTwo() {
    return (
      ['5', '4', '3', '2'].includes(this.firstRank) ||
      ['5', '4', '3', '2'].includes(this.secondRank)
    );
  };

  availableToSelect(combosToAdd) {
    return (
      this.possible &&
      !this.inRange &&
      this._includedInCombosToAdd(combosToAdd)
    );
  };
  _includedInCombosToAdd(combosToAdd) {
    return combosToAdd.includes(this.combo);
  };
  _includesPresetHoleCard(presetHoleCards) {
    return (
      this.__includesCard(presetHoleCards.holeCardOne) ||
      this.__includesCard(presetHoleCards.holeCardTwo)
    );
  };
  _includesPresetCommunityCard(presetCommunityCards) {
    return (
      this.__includesCard(presetCommunityCards.flop1) ||
      this.__includesCard(presetCommunityCards.flop2) ||
      this.__includesCard(presetCommunityCards.flop3) ||
      this.__includesCard(presetCommunityCards.turn) ||
      this.__includesCard(presetCommunityCards.river)
    );
  };
  __includesCard(card) {
    return card === this.firstCard || card === this.secondCard;
  };
  updateManuallySetValue(initialWeight, newWeight, playerState, presetCommunityCards, shortDeck) {
    // for 3bet-cbet explorer
    if (!presetCommunityCards.flopSet) {
      return;
    }

    let passesThroughFiveCardHandFilter = this.passesThroughFiveCardHandFilter(
      playerState.fiveCardHands.checkedHands,
      presetCommunityCards.getArrayForState(playerState.name),
      shortDeck
    );

    if (initialWeight === 0) {
      if (newWeight > 0) {
        if (passesThroughFiveCardHandFilter) {
          if (this.manuallySet === 'removed') {
            this.manuallySet = false;
          }
        } else if (!passesThroughFiveCardHandFilter) {
          if (this.manuallySet === 'removed' || this.manuallySet === false) {
            this.manuallySet = 'added';
          }
        }
      }

      else if (newWeight === 0) {
        // don't change this.manuallySet
      }
    }

    else if (initialWeight > 0) {
      if (newWeight > 0) {
        this.manuallySet = 'added';
      }

      else if (newWeight === 0) {
        if (passesThroughFiveCardHandFilter) {
          if (this.manuallySet === false || this.manuallySet === 'added') {
            this.manuallySet = 'removed';
          }
        } else if (!passesThroughFiveCardHandFilter) {
          if (this.manuallySet === 'added') {
            this.manuallySet = false;
          }
        }
      }
    }

    /*

    previously not in range
      set to be in range
        passes through five card hand filter
          manually set to be removed --> false
        doesn't pass through five card hand filter
          manually set to be removed --> added
          manually set is false --> added
      set to be not in range
        passes through five card hand filter
          manually set to be removed --> removed
        doesn't pass through five card hand filter
          manually set to be removed --> removed
          manually set is false --> false
    previously in range
      set to be in range
        passes through five card hand filter
          manually set is false --> added
          manually set is added --> added
        doesn't pass through five card hand filter
          manually set is added --> added
      set to be not in range
        passes through five card hand filter
          manually set is false --> removed
          manually set is added --> removed
        doesn't pass through five card hand filter
          manually set is added --> false

    */
  };
  passesThroughFiveCardHandFilter(checkedHands, presetCommunityCardsArray, shortDeck) {
    let holeCards;
    let handInfo;
    let drawInfo;

    if (!this.possible) {
      throw new Error('Trying check if a specific hand that isn\'t possible passes through a filter.');
    }

    holeCards = [ this.firstCard, this.secondCard ];

    if (shortDeck) {
      handInfo = getHandInfo.runForShortDeck(_.union(holeCards, presetCommunityCardsArray));
      setSpecificHandInfo.run(handInfo, holeCards, presetCommunityCardsArray, true);
      drawInfo = getDrawInfo.run(holeCards, presetCommunityCardsArray, handInfo, true, true);
    } else {
      handInfo = getHandInfo.run(_.union(holeCards, presetCommunityCardsArray));
      setSpecificHandInfo.run(handInfo, holeCards, presetCommunityCardsArray);
      drawInfo = getDrawInfo.run(holeCards, presetCommunityCardsArray, handInfo, true);
    }

    return utilities.results.isHandInCheckedHands(handInfo, drawInfo, checkedHands);
  };
  getWeightOfPassageThroughFiveCardHandFilters(playerState, presetCommunityCards, shortDeck) {
    let holeCards;
    let handInfo;
    let drawInfo;
    let presetCommunityCardsArray;

    if (!this.possible) {
      throw new Error('Trying to get the weight of passage through filter on a specific hand that isn\'t possible.');
    }

    if (playerState.name === 'afterFlop' && !presetCommunityCards.flopSet) {
      return 0;
    } else if (playerState.name === 'afterTurn' && !presetCommunityCards.turnSet) {
      return 0;
    } else if (playerState.name === 'afterRiver' && !presetCommunityCards.riverSet) {
      return 0;
    }

    holeCards = [ this.firstCard, this.secondCard ];
    presetCommunityCardsArray = presetCommunityCards.getArrayForState(playerState.name);

    if (shortDeck) {
      handInfo = getHandInfo.runForShortDeck(_.union(holeCards, presetCommunityCardsArray));
      setSpecificHandInfo.run(handInfo, holeCards, presetCommunityCardsArray, true);
    } else {
      handInfo = getHandInfo.run(_.union(holeCards, presetCommunityCardsArray));
      setSpecificHandInfo.run(handInfo, holeCards, presetCommunityCardsArray);
    }

    if (!playerState.fiveCardHands.anyCheckedDraws && !playerState.fiveCardHands.anyCheckedCombos) {
      return utilities.results.getWeightOfLargestMatchingFiveCardHand(
        handInfo,
        null,
        playerState.fiveCardHands.checkedMadeHands
      );
    }

    else {
      drawInfo = getDrawInfo.run(holeCards, presetCommunityCardsArray, handInfo, true, shortDeck);

      return utilities.results.getWeightOfLargestMatchingFiveCardHand(
        handInfo,
        drawInfo,
        playerState.fiveCardHands.checkedHands,
      );
    }
  };
  get weightOfPassageThroughManuallySetFilter() {
    if (this.manuallySet === 'added' || this.manuallySet === 'removed') {
      return this.weight;
    }

    return null;
  };
  getWeightToApply(playerState, presetCommunityCards, weightOfPreviousStatesSpecificHand, shortDeck) {
    if (!this.possible) {
      return 0;
    }

    if (typeof this.weightOfPassageThroughManuallySetFilter === 'number') {
      return this.weightOfPassageThroughManuallySetFilter;
    }

    let weightOfPassageThroughFiveCardHandFilters = this.getWeightOfPassageThroughFiveCardHandFilters(playerState, presetCommunityCards, shortDeck);

    return (weightOfPreviousStatesSpecificHand * (weightOfPassageThroughFiveCardHandFilters / 100)).roundToNearestFive();
  };
  matchesFiveCardHand(fiveCardHand, presetCommunityCardsArray, shortDeck) {
    let holeCards;
    let handInfo;
    let drawInfo;

    if (!this.possible) {
      return false;
    }

    holeCards = [ this.firstCard, this.secondCard ];

    if (shortDeck) {
      handInfo = getHandInfo.runForShortDeck(_.union(holeCards, presetCommunityCardsArray));
      setSpecificHandInfo.run(handInfo, holeCards, presetCommunityCardsArray, true);
      drawInfo = getDrawInfo.run(holeCards, presetCommunityCardsArray, handInfo, true, true);
    } else {
      handInfo = getHandInfo.run(_.union(holeCards, presetCommunityCardsArray));
      setSpecificHandInfo.run(handInfo, holeCards, presetCommunityCardsArray);
      drawInfo = getDrawInfo.run(holeCards, presetCommunityCardsArray, handInfo, true);
    }

    return fiveCardHand.isAHit(handInfo, drawInfo);
  };
  clearManuallySetIfPassesThroughFilter(fiveCardHand, presetCommunityCardsArray, shortDeck) {
    if (this.matchesFiveCardHand(fiveCardHand, presetCommunityCardsArray, shortDeck)) {
      this.manuallySet = false;
    }
  };
};



// WEBPACK FOOTER //
// ./src/services/hands/SpecificHand.js